import React from "react";
import {Prompt, useHistory, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {Box, Button, Card, CardContent, Grid, IconButton, Link, Typography} from "@material-ui/core";
import {Alert as Alerts} from "@material-ui/lab";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import {TableView} from "../../Common";
import {SendRecipientsInvite, ViewIllustrations} from "./index";
import ConfirmationDialog from "../../Common/ConfirmationDialog";
import GenericDialog from "../../dialog/GenericDialog";
import {useActions} from "../../../actions";
import * as Alert from "../../../actions/alert";
import * as AccountActions from "../../../actions/account";
import * as GiftorActions from "../../../actions/giftor";
import {RootState} from "../../../reducers";
import {
    useClearRecipientListPage,
    useGetRecipientListPageProps,
    useSetRecipientPageProps,
} from "../../../model/PageProps";
import {getStatusLabel, GiftorStatus, RecipientStatus} from "../utils";
import {RecipientListItem} from "../../../model/giftor";
import {checkRole, isDebug, maskCurrency, noop, sleep, va} from "../../../utils/commonUtils";
import {AccountType, JsEvents} from "../../../constants";


interface RecipientsListProps {
    parentId?: string;
    refresh?: number;
    setLoading: Function;
}


const List = (props: RecipientsListProps) => {
    const PROMPT_MSG = "The Giftor has been added to your client list. If you leave this page the Giftor will not receive an email invitation. To send an invitation, close this message and click the “Send Invite” button.";
    const debugMode = isDebug();
    const params: any = useParams();
    const history = useHistory();
    const {getAccount} = useActions(AccountActions);
    const alertActions = useActions(Alert);
    const gActions = useActions(GiftorActions);
    const propsCarrierId = useGetRecipientListPageProps("carrierId")
    const setPageProp = useSetRecipientPageProps()
    const clearPage = useClearRecipientListPage()
    const {parentId, refresh, setLoading = noop} = props;
    const parentAccount: any = useSelector((state: RootState) => state.account);
    const {user, user: {role}} = useSelector((state: RootState) => state.auth);
    const profileUser = useSelector((state: RootState) => state.profile);
    const isAgent = checkRole(role, [AccountType.Agent]);
    const isGiftorPortal = checkRole(role, [AccountType.Individual, AccountType.Multilife]);

    // State variables
    const [confirmedRecipients, setConfirmedRecipients] = React.useState<RecipientListItem[]>([]);
    const [recipients, setRecipients] = React.useState<RecipientListItem[]>([]);
    const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
    const [confirmDelete, setConfirmDelete] = React.useState(false);
    const [deleteId, setDeleteId] = React.useState<number | null>(null);
    const [confirmSendInvite, setConfirmSendInvite] = React.useState(false);
    const [sending, setSending] = React.useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(isAgent);

    // Determine the parent user ID based on the provided parentId (GiftorDetailsTab - Client Details)
    // or user's ID
    // Furthermore if the user is an Agent, use the ID from params; otherwise, use the current user's ID
    const parentUserId = !!parentId ? parentId : (isAgent ? params.id : user.id);
    const giftorStatus = profileUser && profileUser.client && profileUser.client.giftorStatus;
    let isGiftorUnconfirmed = giftorStatus !== GiftorStatus.Confirmed;

    const handleAddEditRecipient = async (id?: number) => {
        setHasUnsavedChanges(false);
        await sleep(10);
        const key = !!id ? id : `add/${parentUserId}`;
        const recipientAddEditURL = `/giftor/recipient/${key}`;
        history.push(recipientAddEditURL);
    };
    const getUserDetail = async (parentId: string) => {
        const result = await getAccount(parentId, AccountType.Individual);
        if (result && result.client) {
            const clientPolicy = result.client && result.client.clientPolicies && result.client.clientPolicies[0];
            const carrierId = (clientPolicy && clientPolicy.carrierId) || (clientPolicy && clientPolicy.carrier && clientPolicy.carrier.id);
            propsCarrierId != carrierId && setPageProp("carrierId", carrierId);
        }
    };
    const onToggleConfirmSendInvite = () => setConfirmSendInvite(!confirmSendInvite);
    const fetchRecipients = async () => {
        try {
            setLoading(true);
            const result = await gActions.getChildRecipientList({parentUserId});
            setRecipients((result && result.data) || []);
        } catch (err) {
            debugMode && console.error('Failed to fetch recipients', err);
        } finally {
            setLoading(false);
        }
    };
    const handleCancelSend = () => {
        setSelectedIds([]);
        setConfirmedRecipients([]);
        onToggleConfirmSendInvite();
    };
    const handleSendInvite = async () => {
        setHasUnsavedChanges(false);

        const selectedRecipients = recipients.filter(item => selectedIds.includes(item.id) && item.status !== RecipientStatus.UNCONFIRMED);
        if (selectedRecipients.length === 0) {
            const msg = 'No recipients selected. Please select at least one recipient with confirmed status to send an invite.';
            debugMode && console.warn(msg);
            alertActions.setWarning(msg);
            return;
        }

        setConfirmedRecipients(selectedRecipients);
        onToggleConfirmSendInvite();
    };
    const handleSendAgentInvite = async () => {
        setHasUnsavedChanges(false);
        await gActions.triggerGWTAgentEmails({userId: parentUserId});
        history.replace(`/client/add`);
        return;
    };
    const handleCheckboxChange = (id: number, checked: boolean) => {
        setSelectedIds(prev => checked ? [...prev, id] : prev.filter(itemId => itemId !== id));
    };
    const onConfirmDelete = (id: number) => {
        setDeleteId(id);
        setConfirmDelete(true);
    };
    const handleConfirmDelete = async () => {
        if (!deleteId) {
            return;
        }
        try {
            await gActions.deleteChildRecipient(deleteId);
            await fetchRecipients();
        } catch (err) {
            debugMode && console.error('Failed to delete recipient', err);
        } finally {
            setConfirmDelete(false);
            setDeleteId(null);
        }
    };
    const onClickSendInvite = isAgent ? handleSendAgentInvite : handleSendInvite;
    const disableSendInvite: boolean = (isAgent && !parentId) ? false : (selectedIds.length < 1 || isGiftorUnconfirmed);
    const checkAdminIMO = checkRole(role, [AccountType.MasterAdmin, AccountType.Admin, AccountType.IMO, AccountType.ImoMember]);
    const actionsHeader = checkAdminIMO ? "" : "Actions";
    const gridProps = {
        headers: ["", "Name", "Email", "Relationship to Recipient", "Contribution Amount", isGiftorPortal ? "Proposal" : "Illustration",
            ...(isAgent ? [] : ["Status"]),
            actionsHeader],
        list: recipients.map((item, index) => {
            return [
                <>
                    {
                            !checkAdminIMO &&
                            <input
                                    type="checkbox"
                                    disabled={item.status !== RecipientStatus.CONFIRMED}
                                    key={item.id}
                                    checked={selectedIds.includes(item.id)}
                                    onChange={(e) => handleCheckboxChange(item.id, e.target.checked)}
                            />
                    }
                    {
                            checkAdminIMO &&
                            <>{index + 1}</>
                    }
                </>,
                <>
                    {
                        item.status >= RecipientStatus.WaitingForAdminApproval || checkAdminIMO ?
                                `${item.firstName} ${item.lastName}` :
                                <>
                                    <Link
                                            className="cursorPointer"
                                            onClick={() => handleAddEditRecipient(item.id)}
                                    >
                                        {item.firstName} {item.lastName}
                                    </Link>
                                </>
                    }
                </>,
                item.email,
                item.relation,
                maskCurrency(item.contribution),
                !!va(item.userIllustrations) && <ViewIllustrations item={item}/>,
                ...(isAgent ? [] : [getStatusLabel(item.status)]),
                <>
                    {
                            item.status !== RecipientStatus.INVITED && !checkAdminIMO &&
                            <>
                                <IconButton
                                        aria-label="edit-recipient"
                                        className="cursorPointer"
                                        disabled={item.status >= RecipientStatus.WaitingForAdminApproval}
                                        onClick={() => handleAddEditRecipient(item.id)}
                                >
                                    <EditIcon color="action"/>
                                </IconButton>
                                <IconButton
                                        aria-label="delete-recipient"
                                        className="cursorPointer ml10"
                                        disabled={item.status >= RecipientStatus.WaitingForAdminApproval}
                                        onClick={() => onConfirmDelete(item.id)}
                                >
                                    <DeleteIcon className="textRed"/>
                                </IconButton>
                            </>
                    }
                </>,
            ];
        }),
        noRowsMsg: "Click add recipient to get started.",
    };

    React.useEffect(() => {
        fetchRecipients().then(() => {
            // todo NIWD-560
            const giftorStatus = profileUser && profileUser.client && profileUser.client.giftorStatus;
            isGiftorUnconfirmed = giftorStatus !== GiftorStatus.Confirmed;
        });
    }, [parentUserId, refresh]);

    React.useEffect(() => {
        if (isAgent && !parentId) {
            getUserDetail(parentUserId).then();
        }
    }, [parentUserId, isAgent]);

    React.useEffect(() => {
        return () => {
            // it should be call only in parent component
            // we need here because this is used as component on some places. we need to clear it for specific condition
            if (isAgent && !parentId)
                clearPage();
        }
        // Do not add any dependency here
    }, []);

    // Adds prompt popup for unsaved changes when the user navigates away from the page NIWD-644
    React.useEffect(() => {
        if (isAgent && !parentId) {
            const handleBeforeUnload = (e: any) => {
                e.preventDefault();
                e.returnValue = PROMPT_MSG;
                setHasUnsavedChanges(true)
            };
            window.addEventListener(JsEvents.BeforeUnLoad, handleBeforeUnload);
            return () => {
                window.removeEventListener(JsEvents.BeforeUnLoad, handleBeforeUnload);
                setHasUnsavedChanges(false);
            };
        }
    }, []);

    debugMode && console.info({isGiftorPortal, profileUser, parentAccount})

    return (
            <Card className="boxShadow mt20">
                <CardContent>
                    <ConfirmationDialog
                            title="Confirm Delete"
                            description="Are you sure you want to delete this recipient?"
                            isConfirmed={confirmDelete}
                            setIsConfirmed={setConfirmDelete}
                            action={handleConfirmDelete}
                    />

                    {
                            confirmSendInvite &&
                            <GenericDialog
                                    aria-describedby="confirm-send-invite-dialog"
                                    aria-labelledby="confirm-send-invite-dialog"
                                    content={
                                        (
                                                <SendRecipientsInvite
                                                        confirmedRecipients={confirmedRecipients}
                                                        handleCancelSend={handleCancelSend}
                                                        sending={sending}
                                                        setSending={setSending}
                                                />
                                        )
                                    }
                                    disableEscapeKeyDown={sending}
                                    fullWidth
                                    keepMounted
                                    maxWidth="md"
                                    onClose={sending ? noop : onToggleConfirmSendInvite}
                                    open={confirmSendInvite}
                                    title="Complete Giftor Invitation"
                            />
                    }

                    {
                            !parentId && isAgent &&
                            <Prompt message={PROMPT_MSG} when={hasUnsavedChanges}/>
                    }

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="h3" className="mt20 mb10">
                                Recipients List
                                {
                                        !checkAdminIMO &&
                                        <Button
                                                disabled={recipients.length >= 10}
                                                variant="outlined"
                                                className="floatRight mt15"
                                                size="small"
                                                color="primary"
                                                onClick={() => handleAddEditRecipient()} // needs to be anonymous function to avoid it mistaken for edit
                                                startIcon={<AddIcon/>}

                                        >
                                            Add Recipient
                                        </Button>
                                }
                            </Typography>
                            {
                                    !checkAdminIMO && isAgent ?
                                    <Typography variant="body2" color="textPrimary" className="mt10">
                                        Click on the recipient’s name or the pencil icon to edit the recipient details.
                                    </Typography>:
                                    <Typography variant="body2" color="textPrimary" className="mt10">
                                        Click on the recipient’s name or the pencil icon to confirm the recipient details.
                                    </Typography>
                            }
                        </Grid>
                        <Grid item xs={12}>
                            <TableView {...gridProps} />
                        </Grid>
                        {
                                !checkAdminIMO && role !== AccountType.Agent && (
                                        <Grid item xs={12} md={8}>
                                            <Alerts className="mb20 rainbow" variant="outlined" severity="info">
                                                <Typography variant="body2" className="mt0">
                                                    Check the box next to the recipient you want to invite. Recipient status
                                                    must be confirmed to send the invite.<sup className="textRed">*</sup>
                                                </Typography>
                                            </Alerts>
                                        </Grid>
                                )
                        }
                        {
                                !checkAdminIMO &&
                                <Grid container spacing={2} direction="column" alignItems="flex-end" className="mt20">
                                    <Grid item xs={12}>
                                        <Box display="flex" flexDirection="column" alignItems="flex-end">
                                            <Button
                                                    color="primary"
                                                    disabled={disableSendInvite}
                                                    onClick={onClickSendInvite}
                                                    variant="contained"
                                            >
                                                {isAgent ? "Send Invitation" : "Continue"}
                                            </Button>
                                            {
                                                    role !== AccountType.Agent &&
                                                    <Typography variant="caption" className="f14">
                                                        Giftor and Recipient information must be confirmed to send
                                                        invitation. <sup className="colorRed">*</sup>
                                                    </Typography>
                                            }

                                        </Box>
                                    </Grid>
                                </Grid>
                        }
                    </Grid>
                </CardContent>
            </Card>
    );
};

export default List;
